import React, { useContext, useEffect } from 'react'
import { Autosuggest, Button, Cell, Grid, IconButton, InputGroup, ResponsiveTableRow, Row } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import ArrowCaret from '@fs/zion-icon/dist/cjs/icons/ArrowCaret'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import { v4 } from 'uuid'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { UserContext } from '../UserContext'
import { useUserMessagesController } from './useUserMessagesController'
import { isNullOrUndefinedOrEmpty } from '../../../util/Utils'
import { RawData } from '../../common/RawData'
import { convertUTCString } from '../../../util/DateUtil'
import { CisLink } from '../../app/Links'
import { getFrontendBaseUrl } from '../../../util/UrlUtil'
import { DmcExpander } from '../../common/DmcExpander'
import { DmcText } from '../../common/DmcText'
import { getIdElement } from '../../../util/StringUtil'
import { DmcDatePicker } from '../../common/DmcDatePicker/DmcDatePicker'
import DmcPaginator from '../../common/DmcPaginator'

export default () => {}

export function UserMessages() {
  usePageView({ page_detail: 'DMConsole:UserMessages' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.messages'))
  const { currentUser } = useContext(UserContext)
  const { messagesState, messagesActions } = useUserMessagesController(currentUser.dmcUser.cisId)

  useEffect(() => {
    messagesActions.fetchMessages(messagesState.pagination?.currentPage)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (isNullOrUndefinedOrEmpty(messagesState.participants)) {
    return null
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={3}>
        <InputGroup label={t('user.participant', 'Participant')}>
          <SelectParticipant messagesState={messagesState} messagesActions={messagesActions} />
        </InputGroup>
      </Cell>
      <Cell columns={2}>
        <DmcDatePicker
          label={t('startDate', 'Start Date')}
          selectedItem={messagesState.filters.startDate}
          setSelectedItem={(item) => messagesActions.setStartDate(item)}
          disabled={false}
        />
      </Cell>
      <Cell columns={2}>
        <DmcDatePicker
          label={t('endDate', 'End Date')}
          selectedItem={messagesState.filters.endDate}
          setSelectedItem={(item) => messagesActions.setEndDate(item)}
          disabled={false}
        />
      </Cell>
      <Cell columns={5}>
        <InputGroup label={'\xa0'}>
          <Button
            emphasis="medium"
            onClick={() => messagesActions.fetchMessages(messagesState.pagination?.currentPage)}
          >
            Filter
          </Button>
        </InputGroup>
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <RawData state={messagesState} />
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <DmcPaginator
          index={messagesState.pagination.currentPage}
          setIndex={messagesActions.nextPaginatorPage}
          hideResultsPerPage
          pageCount={messagesState.pagination.itemsPerPage}
          length={messagesState.pagination.totalItems}
          topAndBottom
        >
          <MessagesTable messagesState={messagesState} messagesActions={messagesActions} />
        </DmcPaginator>
      </Cell>
    </Grid>
  )
}

function SortingIcon({ messagesState, messagesActions }) {
  if (messagesState.sort.ascending) {
    return <IconButton Icon={ArrowCaret} iconDirection="up" onClick={() => messagesActions.setAscending(false)} />
  }
  return <IconButton Icon={ArrowCaret} iconDirection="down" onClick={() => messagesActions.setAscending(true)} />
}

function MessagesTable({ messagesState, messagesActions }) {
  const { t } = useTranslation()
  return (
    <>
      <Grid>
        <Row columns={2}>
          <DmcText bold text={t('lastModified', 'Last Modified')} alignY="nextToInput" />
          <SortingIcon messagesState={messagesState} messagesActions={messagesActions} alignY="nextToInput" />
        </Row>
        <Row columns={1}>
          <DmcText bold text={t('status.label', 'Status')} />
        </Row>
        <Row columns={3}>
          <DmcText bold text={t('user.messages.Participants', 'Participants')} />
        </Row>
        <Row columns={4}>
          <DmcText bold text={t('user.messages.subject', 'Subject')} />
        </Row>
        <Row columns={2}>
          <DmcText bold text={t('operations', 'Operations')} />
        </Row>
      </Grid>
      <DmcResponsiveTable
        totalColumnCount={5}
        atSizeColumns={{
          default: {
            labels: ['', '', '', '', ''],
            columnWidths: [2, 1, 3, 4, 2],
          },
        }}
      >
        {messagesState.messages.map((message) => {
          return (
            <ResponsiveTableRow
              key={`${message.threadId}`}
              column1={<DmcText text={convertUTCString(message.lastModifiedTime)} />}
              column2={<DmcText text={t(`user.messages.status.${message.userThreadState}`, message.userThreadState)} />}
              column3={<ParticipantColumn message={message} messagesState={messagesState} />}
              column4={<DmcText text={message.subject} />}
              column5={
                <Link
                  to={`${getFrontendBaseUrl(true)}/messaging/mailbox/admin?thread=${message.threadId}`}
                  target="_blank"
                  noScrollTop
                >
                  {t('action.view', 'View')}
                </Link>
              }
            />
          )
        })}
      </DmcResponsiveTable>
    </>
  )
}

function ParticipantColumn({ messagesState, message }) {
  const { t } = useTranslation()
  if (message.participantIds.length > 10) {
    return (
      <DmcExpander
        header={t('user.clickToSeeAllParticipants', 'Click to see all participants')}
        body={
          <>
            {message.participantIds.map((participantId) => (
              <div key={v4()}>
                <CisLink
                  userId={getIdElement(participantId)}
                  userName={messagesState.participants?.[participantId]?.name}
                />
              </div>
            ))}
          </>
        }
      />
    )
  }
  return (
    <div>
      {message.participantIds.map((participantId) => (
        <div key={v4()}>
          <CisLink userId={getIdElement(participantId)} userName={messagesState.participants?.[participantId]?.name} />
        </div>
      ))}
    </div>
  )
}

function SelectParticipant({ messagesState, messagesActions }) {
  const suggestions = Object.keys(messagesState.participants).map((participantKey) => {
    const participant = messagesState.participants[participantKey]
    return {
      key: participant.name + participant.cisId,
      cisId: participant.cisId,
      name: participant.name,
      primaryText: `${participant.name} (${participant.cisId})`,
    }
  })
  return (
    <Autosuggest
      selectedItem={messagesState.filters.participant}
      onSuggestionSelected={(item) => messagesActions.setParticipant(item)}
      suggestions={suggestions}
      clearable
    />
  )
}
